import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { BackIcon } from '../components/icons.js';
import { STATE_DOC_MAX_CHARS, StateDocKind, type StateDoc } from '@jabberr/core';

/** Plain names for the four documents (core's STATE_DOC_LABELS stay for the api and prompts). */
const DOC_NAMES: Record<StateDocKind, string> = {
  production: "What's live now",
  dev: 'Being built',
  backlog: 'Planned',
  feedback: 'What people reported',
};
import { Markdown } from '../components/Markdown.js';
import { ErrorBox, Spinner } from '../components/ui.js';
import { api, errorMessage } from '../lib/api.js';
import { relativeTime } from '../lib/format.js';
import { useDrawer } from '../state/drawer.js';

const HELP: Record<StateDocKind, string> = {
  production: 'What is released: what clients and users can see today.',
  dev: 'What is built or in progress and not yet released.',
  backlog: 'What is planned. The source of truth lives with the project, not here.',
  feedback: 'What users have reported, with the release each report was made against.',
};

/** The four state documents. Normally CI and the nightly job send them; a member can also publish by hand. */
export function StatePage() {
  const { projectId = '' } = useParams();
  const drawer = useDrawer();
  const [docs, setDocs] = useState<StateDoc[] | null>(null);
  const [editing, setEditing] = useState<StateDocKind | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDocs((await api.state.get(projectId)).docs);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [projectId]);

  useEffect(() => {
    setDocs(null);
    setEditing(null);
    void load();
    drawer.setInfo({ projectId, title: "What's live and being built" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, projectId]);

  const publish = async (kind: StateDocKind): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.state.put(projectId, kind, { content: draft });
      setEditing(null);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <Link to={`/p/${projectId}/knows`} className="back-link">
          <BackIcon />
          What Jabberr knows
        </Link>
          <h1>What's live and being built</h1>
        </div>
      </header>
      <p className="muted state-docs-lede">
        Short documents about where the project stands. Each release sends them automatically; you can also write one here by hand. A conversation
        uses the versions that existed when it started and is told when one has moved on.
      </p>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {docs === null ? (
        <Spinner label="Loading" />
      ) : (
        <div className="state-docs">
          {StateDocKind.options.map((kind) => {
            const doc = docs.find((d) => d.kind === kind);
            const isEditing = editing === kind;
            const headingId = `state-doc-${kind}`;
            return (
              <section key={kind} className="card state-doc-card" aria-labelledby={headingId}>
                <header className="state-doc-head">
                  <div className="state-doc-title">
                    <h2 id={headingId} className="state-doc-name">
                      {DOC_NAMES[kind]}
                    </h2>
                    {doc ? <span className="chip">version {doc.version}</span> : <span className="chip warn">none yet</span>}
                  </div>
                  {isEditing ? null : (
                    <button
                      type="button"
                      className="btn btn-soft"
                      onClick={() => {
                        setDraft(doc?.content ?? '');
                        setEditing(kind);
                      }}
                    >
                      {doc ? 'Edit' : 'Write'}
                    </button>
                  )}
                </header>
                <p className="state-doc-desc muted small">
                  {HELP[kind]}
                  {doc ? ` Sent by ${doc.source === 'manual' ? 'hand' : doc.source}${doc.sourceRef ? ` (${doc.sourceRef})` : ''}, ${relativeTime(doc.createdAt)}.` : ''}
                </p>
                {isEditing ? (
                  <div className="state-doc-editor">
                    <textarea rows={14} value={draft} onChange={(e) => setDraft(e.target.value)} aria-label={DOC_NAMES[kind]} />
                    <div className="row wrap">
                      <button type="button" className="btn btn-primary" disabled={busy || draft.length > STATE_DOC_MAX_CHARS} onClick={() => void publish(kind)}>
                        Publish
                      </button>
                      <button type="button" className="btn" onClick={() => setEditing(null)}>
                        Cancel
                      </button>
                      <span className={`small ${draft.length > STATE_DOC_MAX_CHARS ? 'error-text' : 'muted'}`}>
                        {draft.length} of {STATE_DOC_MAX_CHARS} characters
                      </span>
                    </div>
                  </div>
                ) : doc && doc.content ? (
                  <div className="state-doc-body">
                    <Markdown text={doc.content} />
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
